import { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchQuickAlertsData,
  selector as listQuickAlertsApi,
} from "./listQuickAlertsSlice";

import { Status } from "../../../../common/constants";
import { useSpinner } from "../../../../customHooks";
import { actions as spinnerActions } from "../../../../components/ui/spinner/spinnerSlice";
import { saveQuickAlertsOrderData } from "./saveQuickAlertsOrderSlice";
import useNotification from "../../../../components/notification/useNotification";
import { isSuccessResponse } from "../../../../common/utils";
import {QUICK_ALERT_PAGE_LIMIT} from "../../../../common/constants";
import { createQuickAlerts } from "../createQuickAlerts/createQuickAlertsSlice";
import {
  IInitialSliceState,
} from "../../../../common/types/commonTypes";

interface ICreateQuickAlertsResponseState {
  createQuickAlertsApi: IInitialSliceState;
}

interface IEditQuickAlertsResponseState {
  editQuickAlertsApi: IInitialSliceState;
}

const useListQuickAlerts = (
  search?: string,
  severity?: string,
  group?: string
) => {
  const dispatch: any = useDispatch();
  const quickalerts = useSelector(listQuickAlertsApi);
  const [quickAlertsData, setQuickAlertsData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [activeAccordion, setActiveAccordion] = useState<number | null>(null);
  const pageRequestLock = useRef(false);

  const observer = useRef<any>(null);

  const { showSuccessNotification, showWarningNotification } =
    useNotification();

  useSpinner();

  const createQuickAlertsResponse = useSelector(
    (state: ICreateQuickAlertsResponseState) => state.createQuickAlertsApi
  );

  const editQuickAlertsResponse = useSelector(
    (state: IEditQuickAlertsResponseState) => state.editQuickAlertsApi
  );

  useEffect(() => {
    dispatch(spinnerActions.setApiStatus(quickalerts.status));
  }, [quickalerts.status, dispatch]);

  useEffect(() => {
    if (!quickalerts.quickalerts?.values) return;

    const { values, totalPages } = quickalerts.quickalerts;

    setQuickAlertsData((prev) =>
      page === 1 ? values : [...prev, ...values]
    );

    setTotalPages(totalPages);

    setHasMore(page < totalPages);
  }, [quickalerts.quickalerts]);

  useEffect(() => {
    if (createQuickAlertsResponse.status === Status.SUCCESS || editQuickAlertsResponse.status === Status.SUCCESS) {
      reloadQuickAlertList();
    }
  }, [createQuickAlertsResponse.status, editQuickAlertsResponse.status]);
  
  
  useEffect(() => {
    pageRequestLock.current = false;
    setQuickAlertsData([]);
    setHasMore(true);
    setTotalPages(null);
    setPage(1);
  }, [search, severity, group]);

  useEffect(() => {
    if (totalPages !== null && page > totalPages) return;
  
    pageRequestLock.current = true;
  
    dispatch(
      fetchQuickAlertsData({
        page,
        limit: QUICK_ALERT_PAGE_LIMIT,
        search,
        severity,
        group,
      })
    ).finally(() => {
      pageRequestLock.current = false;
    });
  }, [page, search, severity, group, reloadKey]);

  const lastItemRef = useCallback(
    (node: HTMLElement | null) => {
      if (!node) return;
      if (isDragging) return;
      if (pageRequestLock.current) return;
      if (totalPages !== null && page >= totalPages) return;
  
      if (observer.current) observer.current.disconnect();
  
      observer.current = new IntersectionObserver(
        (entries) => {
          if (
            entries[0].isIntersecting &&
            !pageRequestLock.current &&
            (totalPages === null || page < totalPages)
          ) {
            pageRequestLock.current = true;
            setPage((prev) => prev + 1);
          }
        },
        { threshold: 0.1 }
      );
  
      observer.current.observe(node);
    },
    [isDragging, page, totalPages]
  );

  const toggleAccordion = (index: number) =>
    setActiveAccordion((prev) => (prev === index ? null : index));

  const closeAccordion = () => setActiveAccordion(null);

  const reloadQuickAlertList = () => {
    pageRequestLock.current = false;
    setQuickAlertsData([]);
    setHasMore(true);
    setTotalPages(null);
    setPage(1);
    setReloadKey((prev) => prev + 1);
  };

  const reorderAlerts = async (reordered: any[]) => {
    setQuickAlertsData(reordered);
    
    const alertOrder = reordered.map((alert) => alert._id);

    try {
      dispatch(spinnerActions.setApiStatus(Status.PENDING));
      const response = await dispatch(
        saveQuickAlertsOrderData(alertOrder)
      ).unwrap();

      dispatch(spinnerActions.setApiStatus(Status.SUCCESS));

      if (isSuccessResponse(response)) {
        reloadQuickAlertList();
        showSuccessNotification(response.message);
      } else {
        showWarningNotification(response.message);
      }
    } catch (err) {
      dispatch(spinnerActions.setApiStatus(Status.FAILED));
    }
  };
  
  return {
    quickAlertsData,
    setQuickAlertsData,
    activeAccordion,
    closeAccordion,
    reorderAlerts,
    lastItemRef,
    hasMore,
    status: quickalerts.status,
    setIsDragging,
    reloadQuickAlertList,
  };
};

export default useListQuickAlerts;
